/**
 * Visual tokens for the coding-screen shell. Imports NOTHING (plain string/number literals only) so it
 * satisfies the project_starburst isolation boundary guard; `var(--color-x, #hex)` is just a string to
 * that guard.
 *
 * Values are eyeballed from `docs/tickets/CDMX-18286/assets/empty-ui.png`, not pixel-verified.
 *
 * - Lengths are CSS strings, never bare numbers: Chakra resolves a bare number against its theme scale
 *   (`theme.sizes[44]` is `11rem`, not 44px). Exceptions are noted per token.
 * - The app forces a 14px root (`src/styles/cmx.scss`), so `px / 16` rem conversions render 12.5%
 *   SHORT. Tokens that must hit an exact size are absolute px. The remaining rem tokens
 *   (`CASE_CONTEXT_BAR_H`, `FOOTER_H`, `RAIL_WIDTH`) still carry this discrepancy.
 * - Colors are `var(--color-<biome-token>, <hex>)`. The var is defined by `ui-components/dist/index.css`;
 *   the hex is the fallback for Storybook/jsdom, where it is absent. Where no biome token matches, the
 *   token is a plain literal.
 */

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

export const PAGE_BG = 'var(--color-structural-300, #f3f3f7)';

export const TOPBAR_BG = '#F7F3FC';

export const TOPBAR_BORDER = 'color-mix(in oklab, #8B1FC8 14%, transparent)';

export const TOPBAR_BORDER_WIDTH = '1px';
export const TOPBAR_BORDER_STYLE = 'solid';

export const BAR_BG = 'var(--color-structural-0, #ffffff)';

export const BAR_BORDER = 'var(--color-chips-grey-50, #e7e7ee)';

export const FIELD_LABEL_BG = '#ededf0';

export const FIELD_VALUE_BG = 'var(--color-structural-0, #ffffff)';

export const FIELD_LABEL_DIVIDER = 'var(--color-chips-grey-50, #ececef)';

export const FIELD_LABEL_FONT_SIZE = '11px';
export const FIELD_LABEL_FONT_WEIGHT = 700;
export const FIELD_LABEL_LETTER_SPACING = '0.06em';

export const CASE_CONTEXT_TITLE_LETTER_SPACING = '0.08em';

export const CARD_BG = 'var(--color-structural-0, #ffffff)';

export const CARD_BORDER = '#cfc8d8';

export const CARD_RADIUS = '10px';

export const CARD_SHADOW = '0 1px 3px rgba(16, 16, 40, 0.06)';

export const STRIP_BG = 'var(--color-structural-100, #f9f9fc)';

export const NAV_STRIP_BG = '#f9f8fa';

export const PAGE_FOOTER_BG = '#f8f9fa';

export const STRIP_BORDER = 'var(--color-chips-grey-50, #ededf2)';

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

export const TOPBAR_H = '37px';

export const PATIENT_BAR_H = '48px';

export const CASE_CONTEXT_BAR_H = '3.5rem';

export const STRIP_H = '34px';

export const TAB_FONT_SIZE = '12px';

export const TAB_WEIGHT = 500;

export const TAB_WEIGHT_ACTIVE = 600;

export const STRIP_PAD_X = '6px';

export const TAB_GAP = '4px';

export const TAB_PAD_Y = '4px';
export const TAB_PAD_X = '10px';

export const TAB_ACTIVE_BG = 'blue.50';

export const TAB_ACTIVE_BORDER = 'blue.200';

export const TAB_RADIUS = 'md';

/** Longhands, never `border: '1px solid'`. The shorthand resets color to `currentColor` (black
 *  borders on unselected tabs), and Chakra emits props in its own order, so `borderColor="transparent"`
 *  can't be relied on to win. Border is always present with only its color changing, so tabs don't
 *  shift on selection. */
export const TAB_BORDER_WIDTH = '1px';
export const TAB_BORDER_STYLE = 'solid';

/** All three tab strips share one pill geometry (`TAB_PAD_*`, `TAB_RADIUS`, `TAB_ACTIVE_*`). Don't
 *  add a second tab geometry without changing all three together. */

export const FOOTER_H = '2.5rem';

/** Local on purpose: a bare number is unsafe in Chakra props. Use `RAIL_WIDTH`. */
const RAIL_WIDTH_REM = 3.5;

export const RAIL_WIDTH = `${RAIL_WIDTH_REM}rem`;

export const DOC_BODY_PAD = '24px 32px';

export const WORKSPACE_PAD = '16px 28px';

export const WORKSPACE_REGION_BG = '#e4e2e9';

// ---------------------------------------------------------------------------
// Spacing
// ---------------------------------------------------------------------------

/** `ResizableSplit` separator width. A bare number because its consumer (`gutterPx: number`) only
 *  interpolates it into `calc(...)`. Matches the prototype's `.workspace { gap: 16px }`; here the
 *  gutter IS the gap and holds the drag handle.
 *
 *  Deliberately px, not rem: `clampPct.ts` does exact pixel arithmetic on `getBoundingClientRect()`
 *  values. A rem gutter would need a live px re-derivation (a behavior change to `ResizableSplit`) and
 *  risks the pixel-exact assertions in `ResizableSplit.test.tsx`. */
export const GUTTER_PX = 16;

/** A bare Chakra spacing-scale int on purpose (`5` = 1.25rem). Don't hand it to a prop wanting an
 *  exact px string. Consumed by `PageFooter` and `PatientSummaryBar` `px`. `CodingScreenTemplate`'s
 *  root sets `p={SHELL_PAD}` but its later `padding={0}` wins, so the root renders no padding. */
export const SHELL_PAD = 5;

// ---------------------------------------------------------------------------
// Warm neutral text + surfaces (shared)
// ---------------------------------------------------------------------------

export const INK = '#1a1818';
export const INK_SOFT = '#4a4751';
export const INK_MUTED = '#7a7682';
export const INK_FAINT = '#a5a1ab';
export const SURFACE_WARM = '#f9f8fa';
export const POPOVER_SHADOW = '0 14px 36px -14px rgba(0, 0, 0, 0.4), 0 2px 8px -4px rgba(0, 0, 0, 0.18)';
export const POPOVER_RADIUS = '9px';
export const POPOVER_BORDER = 'var(--color-chips-grey-50, #e2e0e5)';

// ---------------------------------------------------------------------------
// Hold modal colors (CDMX-18412)
// ---------------------------------------------------------------------------

export const HOLD_COLORS = {
  border: 'var(--color-chips-grey-50, #ececef)',
  closeHoverBg: 'var(--color-chips-grey-50, #f0f0f2)',
  iconBg: 'var(--color-chips-yellow-50, #fff4d6)',
  chipSelected: 'var(--color-chips-plum-500, #875882)',
  error: 'var(--color-alert-500, #e50000)',
  iconBar: '#c99a3a',
  inputBg: '#fafaf7',
  noteHoverBorder: '#e0e0e4',
  inkSoft: INK_SOFT,
  ink: INK,
  inkMuted: INK_MUTED,
  inkFaint: INK_FAINT
} as const;

// ---------------------------------------------------------------------------
// Case-action modal radii (CDMX-18412 Hold, Transfer, ...)
// ---------------------------------------------------------------------------

export const CASE_ACTION_RADIUS = {
  modal: '14px',
  tile: '9px',
  control: '8px',
  small: '6px'
} as const;

// ---------------------------------------------------------------------------
// Chip colors (the "Day X of Y" chip and its concurrent-stay variant)
// ---------------------------------------------------------------------------

export const CHIP_COLORS = {
  primary: 'var(--primary, #6b4bd4)',
  primaryFaint: 'var(--primary-faint, #efeafc)',
  primarySoft: 'var(--primary-soft, #efe1f8)',
  surface: 'var(--surface, #ffffff)',
  warn: 'var(--warn, #f5a73f)',
  warnFaint: 'var(--warn-faint, #ffe9cc)',
  warnBorderSoft: 'color-mix(in srgb, var(--warn, #f5a73f) 50%, transparent)',
  warnGlow: '0 0 0 3px rgba(245, 167, 63, 0.3)'
} as const;

export const CHIP_BORDER = '#d6d4d9';

export const CHIP_BORDER_HOVER = '#000000';

// ---------------------------------------------------------------------------
// Encounter metric badge colors (Aging/Codable Days pill in the Encounter popover)
// ---------------------------------------------------------------------------

export const ENCOUNTER_METRIC_COLORS = {
  bg: 'var(--color-chips-red-50, #ffedec)',
  text: 'var(--color-chips-red-500, #c83729)'
} as const;

// ---------------------------------------------------------------------------
// Sibling-case colors (the "Concurrent cases" list in the Encounter popover)
// ---------------------------------------------------------------------------

export const SIBLING_STATUS_COLORS = {
  done: {
    text: 'var(--color-success-500, #42a45c)',
    bg: 'var(--color-success-300, #ccffda)'
  },
  auto: {
    text: 'var(--color-success-500, #42a45c)',
    bg: 'var(--color-success-300, #ccffda)'
  },
  current: { text: 'var(--ai, #6b4bd4)', bg: 'var(--ai-faint, #efeafc)' },
  queued: { text: INK_MUTED, bg: 'var(--panel-2, #eceef2)' }
} as const;

export const SIBLING_ROW_COLORS = {
  surface: CHIP_COLORS.surface,
  panel2: 'var(--panel-2, #eceef2)',
  roleText: INK_SOFT,
  detailText: INK_FAINT
} as const;

export const MONO_STACK = '"JetBrains Mono", monospace';

export const SANS_STACK = "'Inter', system-ui, sans-serif";

// ---------------------------------------------------------------------------
// Provider-details drawer geometry (Encounter's provider-details Drawer)
// ---------------------------------------------------------------------------

/**
 * `headerX`/`bodyX` equal Chakra's stock `DrawerHeader`/`DrawerBody` inline padding (scale `6` =
 * `1.5rem`) resolved on the 14px root: 21px. Hardcoded because `project_starburst` can't import
 * `@codametrix/themes`. One object so header and body can't drift apart.
 */
export const PROVIDER_DRAWER_GEOMETRY = {
  headerX: '21px',
  headerTop: '40px',
  headerBottom: '20px',
  headerContentBottom: '8px',
  bodyX: '21px',
  bodyTop: '0px',
  bodyBottom: '40px',
  closeButtonInset: '28px'
} as const;

// ---------------------------------------------------------------------------
// Transfer modal colors
// ---------------------------------------------------------------------------

export const TRANSFER_COLORS = {
  border: 'var(--color-chips-grey-50, #ececef)',
  closeHoverBg: 'var(--color-chips-grey-50, #f0f0f2)',
  iconBg: 'var(--color-chips-green-50, #dfeee4)',
  iconArrow: 'var(--color-chips-green-700, #1f5c46)',
  chipSelected: 'var(--color-chips-plum-500, #875882)',
  error: 'var(--color-alert-500, #e50000)',
  inputBg: '#fafaf7',
  noteHoverBorder: '#e0e0e4',
  optionActiveBg: 'var(--color-chips-green-50, #eef6f1)',
  groupHeaderBg: '#f4f4f6',
  inkSoft: INK_SOFT,
  ink: INK,
  inkMuted: INK_MUTED,
  /** Warm light-grey -- the faint field hints ("optional" / "required"). Shared `INK_FAINT`. */
  inkFaint: INK_FAINT,
  /** Availability tag fill. biome chips-grey-50. */
  availabilityBg: 'var(--color-chips-grey-50, #f0f2f4)',
  /** Availability tag text. biome chips-grey-500 — 6.74:1 on availabilityBg (WCAG 1.4.3). */
  availabilityFg: 'var(--color-chips-grey-500, #45556e)'
} as const;

// ---------------------------------------------------------------------------
// Exclude modal colors
// ---------------------------------------------------------------------------

/**
 * Each `var()` carries its REAL token value as the fallback, so Storybook matches production.
 *
 * `warnInk`/`danger`/`dangerHover` are literals because biome's `chips-brown-500` (#c37c6a) fails WCAG
 * 1.4.3 (2.91:1 as text on `chips-brown-50`, 3.28:1 under a white label) and the family has no darker
 * step. Measured ratios are recorded per key.
 */
export const EXCLUDE_COLORS = {
  border: 'var(--color-chips-grey-50, #f0f2f4)',
  closeHoverBg: 'var(--color-chips-grey-50, #f0f2f4)',
  error: 'var(--color-alert-500, #e50000)',
  /** White label on it: 5.60:1. */
  chipSelected: 'var(--color-chips-plum-500, #875882)',
  inputBg: '#fafaf7',

  // -- Exclude glyph tile ---------------------------------------------------
  /** The mock's tile is lavender-blue (#efedf8); biome has no such family, so this DELIBERATELY renders
   *  plum. A reviewed look change, not a slip. */
  iconBg: 'var(--color-chips-plum-50, #f3eaf2)',
  /** 4.77:1 on `iconBg` (WCAG 1.4.11 needs 3:1). */
  iconStroke: 'var(--color-chips-plum-500, #875882)',

  // -- "Heavy action" warning banner ---------------------------------------
  warnBg: 'var(--color-chips-brown-50, #feeeea)',
  /** 2.91:1 on the fill; fine for a decorative container edge (outside WCAG 1.4.11). */
  warnBorder: 'var(--color-chips-brown-500, #c37c6a)',
  /** 6.60:1 on `warnBg`. */
  warnInk: '#8a3f28',

  // -- Destructive primary button ------------------------------------------
  /** White label: 5.25:1. */
  danger: '#a8543c',
  /** White label: 6.93:1. */
  dangerHover: '#8d4531',

  // -- Warm-ink scale (inkMuted/inkFaint differ from HOLD_COLORS for contrast) --
  /** 8.0:1 on white. */
  inkSoft: INK_SOFT,
  ink: INK,
  /** 5.19:1 on white. */
  inkMuted: '#6f6b77',
  /** 4.67:1 on white. Kept lighter than `inkMuted` so the hierarchy stays monotonic. */
  inkFaint: '#767281',
  /** Foreground on `danger` and checked reason pills. Named so the contrast ratios above are measured
   *  against a value inside the palette. */
  onFilled: '#ffffff'
} as const;

// ---------------------------------------------------------------------------
// Exclude geometry
// ---------------------------------------------------------------------------

export const EXCLUDE_SIZES = {
  triggerTile: '25px',
  /** Differs from Hold's on purpose: `HoldModal` uses `boxSize="2.75rem"`, which renders 38.5px on the
   *  14px root despite its comment claiming 44px. This is the corrected value; fixing Hold is a
   *  follow-up, so the two header tiles currently differ by 5.5px. */
  headerTile: '44px'
} as const;

/** Bare numbers for `<svg width|height>`. Separate from `EXCLUDE_SIZES` so nobody passes one to a
 *  Chakra prop and gets a scale lookup (`boxSize={24}` is `6rem`). */
export const EXCLUDE_GLYPH_PX = {
  trigger: 14,
  header: 18,
  warn: 16
} as const;

// ---------------------------------------------------------------------------
// Flag chip colors (the Case Context Bar's Flags field)
// ---------------------------------------------------------------------------

export const COMPACT_FIELD_LABEL_PAD = '5px 9px 5px 10px';
export const FIELD_LABEL_PAD = '5px 9px 5px 10px';
export const FIELD_VALUE_PAD = '5px 12px 5px 10px';
export const COMPACT_FIELD_VALUE_PAD = '3px 7px';

/** Keyed by tone. All three are defined though `toFlagsInfoFromCaseRun` only emits `warn` today.
 *  `warn.bg` is a product-specified cream (#FDF9ED), not biome's peach `warning-300`. Borders are
 *  `CHIP_BORDER` (hover: `CHIP_BORDER_HOVER`). */
export const FLAG_CHIP_COLORS = {
  strong: {
    bg: CHIP_COLORS.primaryFaint,
    fg: CHIP_COLORS.primary,
    border: CHIP_BORDER
  },
  warn: {
    bg: '#FDF9ED',
    fg: CHIP_COLORS.warn,
    border: CHIP_BORDER
  },
  info: { bg: 'var(--panel-2, #eceef2)', fg: INK_SOFT, border: CHIP_BORDER }
} as const;

export const FLAG_CHIP_LABEL_FG = '#b08432';

// ---------------------------------------------------------------------------
// Routing Context (the case-context bar's right-pinned "why routed" tag)
// ---------------------------------------------------------------------------

/** `#8b1fc8` is the reference's `--primary`, kept literal per product direction. NOT
 *  `CHIP_COLORS.primary` (`#6b4bd4`). */
export const ROUTING_CONTEXT_COLORS = {
  fg: '#8b1fc8',
  bg: 'color-mix(in srgb, #8b1fc8 8%, transparent)',
  border: CHIP_COLORS.primarySoft
} as const;

export const ROUTING_CONTEXT_FONT_SIZE = '11px';
export const ROUTING_CONTEXT_FONT_WEIGHT = 700;
export const ROUTING_CONTEXT_LETTER_SPACING = '0.07em';

// ---------------------------------------------------------------------------
// Vertical divider (`molecules/VerticalDivider`)
// ---------------------------------------------------------------------------

export const VERTICAL_DIVIDER_COLOR = 'var(--border-strong, #c9c8ce)';

export const VERTICAL_DIVIDER_W = '1px';
export const VERTICAL_DIVIDER_H = '13px';
export const VERTICAL_DIVIDER_MX = '2px';
